import { Box, List, ListItem, Stack, Typography } from '@mui/material'
import { i18WithDefault as t } from '@shared/locales'
import { generateBenefitDisplayName } from '@shared/memberships'
import { BenefitAdminDto, BenefitClientDto } from 'service-api/src/types/api'
import { getBenefitDiscount, sortBenefits } from './helper'
import { formatNumberAsPrice } from 'shared'

type BenefitsListProps = {
  benefits: BenefitAdminDto[] | BenefitClientDto[]
  layout?: 'default' | 'columns'
}

const BenefitRow = ({ benefit }: { benefit: BenefitAdminDto | BenefitClientDto }) => (
  <ListItem disablePadding sx={{ display: 'list-item', listStyle: 'disc', mb: 0.5 }}>
    <Typography component="span" variant="body1bold">
      {benefit.type === 'WALLET_LOAD'
        ? `${t('membership.benefit.walletLoad')} - `
        : benefit.qty
          ? `${benefit.qty} x `
          : `${t('membership.benefit.unlimited')} - `}
    </Typography>
    <Typography component="span" variant="body1bold">
      {benefit.type === 'WALLET_LOAD'
        ? formatNumberAsPrice(benefit.amount, ',', 2, !Number.isInteger(benefit.amount))
        : `${getBenefitDiscount(benefit)} ${t('membership.benefit.discount')} `}
    </Typography>
    <Typography component="span">{generateBenefitDisplayName(benefit)}</Typography>
  </ListItem>
)

export const BenefitsList = ({ benefits, layout = 'default' }: BenefitsListProps) => {
  const sortedBenefits = sortBenefits(benefits)

  if (layout === 'default') {
    return (
      <List sx={{ pl: 4 }}>
        {sortedBenefits.map((benefit) => (
          <BenefitRow key={benefit.id} benefit={benefit} />
        ))}
      </List>
    )
  }

  const columns: BenefitAdminDto[][] | BenefitClientDto[][] = []
  for (let i = 0; i < sortedBenefits.length; i += 4) {
    columns.push(sortedBenefits.slice(i, Math.min(i + 4, sortedBenefits.length)))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {columns.map((column, colIndex) => (
        <Box
          key={`column-${colIndex}`}
          sx={{
            flex: '1 1 50%',
            maxWidth: '100%',
            '@media (min-width: 600px)': { maxWidth: 'calc(50% - 8px)' },
          }}
        >
          <Stack>
            {column.map((benefit) => (
              <List key={benefit.id} sx={{ pl: 4, m: 0 }} disablePadding>
                <BenefitRow benefit={benefit} />
              </List>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  )
}
